/**
 * Main menu: game setup form, start game handler.
 */

import type { WeaponKey, PlayerLoadout, PlanetSizePreset, MapLayout, StarType, SystemPreset, GameMode, EnvironmentKey, ItemKey } from '../types';
import { WEAPONS, WEAPON_KEYS, TEAMS, BOT_PRESETS, BOT_SKILL_LEVELS, SYSTEM_PRESETS, STAR_TYPES, JUMPPACK_FUEL_COST, DELIVERY_COST_MULTIPLIER, MODE_PRESET_DESCRIPTIONS, MODE_FLAVOR_DESCRIPTIONS, MODE_RULES, HEAL_ITEMS, QUICK_PLAY_PRESETS } from '../constants';
import { fx, cur, weaponDisplayName, itemDisplayName, applyFlavorDom, setFlavorEnabled, isFlavorEnabled, updateFlavorToggles } from './flavor';
import { isDevMode, setDevMode, updateDevToggles } from './devMode';
import { actionScale, setActionScale, updateActionScaleToggles } from './actionScale';
import { PREVIEW_QUALITY_LEVELS, kitPreset, applyKitTiers, KIT_PRESET_INFO, KIT_PRESETS } from '../core/unitModifiers';
import type { KitPresetKey } from '../core/unitModifiers';
import { computeBotLoadout } from '../ai/bot';
import { SeededRandom } from '../core/random';
import { validateModeConfig, validateTeamSetup, getModePreset, hasBlockingWarnings } from '../core/configValidation';
import type { ConfigWarning } from '../types';
import { composeModeConfig } from '../core/modes';
import { theme } from './theme';

// Setup types live in types.ts (core/mapgen and network/protocol need them without depending on the UI)
import type { BotSlotOverride, UnitSlotConfig, MenuOptions, ResolutionPace, TurnFlow, UnitKit, LauncherModKey, SuitModKey } from '../types';
import { defaultUnitKit } from '../core/unitModifiers';
import { botTeamsAfterTeamCountChange, botUnitsAfterUnitsChange } from './setupRules';
export type { BotSlotOverride, UnitSlotConfig, MenuOptions };

/**
 * Read allowed weapons from armory restriction checkboxes.
 * Returns undefined if all are checked (no restrictions), or the allowed list.
 */
function readAllowedWeapons(): WeaponKey[] | undefined {
  const checked: WeaponKey[] = [];
  let allChecked = true;
  for (const key of PURCHASABLE_WEAPONS) {
    const cb = document.getElementById(`opt-armory-${key}`) as HTMLInputElement | null;
    if (!cb || cb.checked) {
      checked.push(key);
    } else {
      allChecked = false;
    }
  }
  if (allChecked) return undefined; // no restrictions
  return checked;
}

/**
 * Read allowed camera modes from config checkboxes.
 * Returns undefined if all are checked (no restrictions), or the allowed list.
 */
function readPreviewQuality(): number {
  const sel = document.getElementById('opt-preview') as HTMLSelectElement | null;
  const key = (sel?.value ?? 'full') as keyof typeof PREVIEW_QUALITY_LEVELS;
  return PREVIEW_QUALITY_LEVELS[key] ?? 1;
}

function readVisibility(): 'fog' | 'free' {
  const sel = document.getElementById('opt-visibility') as HTMLSelectElement | null;
  return sel?.value === 'free' ? 'free' : 'fog';
}
function readTurnFlow(): TurnFlow {
  const sel = document.getElementById('opt-turn-flow') as HTMLSelectElement | null;
  const v = sel?.value;
  return v === 'batched' || v === 'simultaneous' || v === 'allPlan' ? v : 'perUnit';
}
function readResolutionPace(): ResolutionPace {
  const sel = document.getElementById('opt-resolution') as HTMLSelectElement | null;
  const v = sel?.value;
  return v === 'relaxed' || v === 'brisk' || v === 'fastForward' ? v : 'full';
}

export function getMenuOptions(): MenuOptions {
  const playersEl = document.getElementById('opt-players') as HTMLSelectElement | null;
  const botsEl = document.getElementById('opt-bots') as HTMLSelectElement | null;
  const planetsEl = document.getElementById('opt-planets') as HTMLSelectElement | null;
  const seedEl = document.getElementById('opt-seed') as HTMLInputElement | null;
  const cbpEl = document.getElementById('opt-cbp') as HTMLInputElement | null;

  const numPlayers = playersEl ? parseInt(playersEl.value, 10) : 2;
  const starsEl = document.getElementById('opt-stars') as HTMLSelectElement | null;
  const wormholesEl = document.getElementById('opt-wormholes') as HTMLSelectElement | null;

  // Collect player names (empty string means use default)
  const playerNames: string[] = [];
  const teamIndices: number[] = [];
  for (let i = 0; i < 4; i++) {
    const nameEl = document.getElementById(`opt-name-${i}`) as HTMLInputElement | null;
    playerNames.push(nameEl?.value.trim() || '');
    const teamEl = document.getElementById(`opt-team-${i}`) as HTMLSelectElement | null;
    teamIndices.push(teamEl ? parseInt(teamEl.value, 10) : i);
  }

  // Bot difficulty & personality (team defaults)
  const botDiffEl = document.getElementById('opt-bot-difficulty') as HTMLSelectElement | null;
  const botPersEl = document.getElementById('opt-bot-personality') as HTMLSelectElement | null;
  const botDifficulty = botDiffEl?.value || 'medium';
  const botPersonality = botPersEl?.value || 'balanced';

  // Per-bot overrides
  const botOverrides: BotSlotOverride[] = [];
  for (let i = 0; i < 4; i++) {
    const diffEl = document.getElementById(`opt-bot-difficulty-${i}`) as HTMLSelectElement | null;
    const persEl = document.getElementById(`opt-bot-personality-${i}`) as HTMLSelectElement | null;
    botOverrides.push({
      difficulty: diffEl?.value || 'default',
      personality: persEl?.value || 'default',
    });
  }

  // Team config fields
  const numTeamsEl = document.getElementById('opt-num-teams') as HTMLSelectElement | null;
  const botTeamsEl = document.getElementById('opt-bot-teams') as HTMLSelectElement | null;
  const unitsPerTeamEl = document.getElementById('opt-units-per-team') as HTMLSelectElement | null;
  const botUnitsEl = document.getElementById('opt-bot-units') as HTMLSelectElement | null;
  const turnOrderEl = document.getElementById('opt-turn-order') as HTMLSelectElement | null;
  const ffEl = document.getElementById('opt-friendly-fire') as HTMLSelectElement | null;
  const datalinkedEl = document.getElementById('opt-datalinked-los') as HTMLInputElement | null;

  const numTeams = numTeamsEl ? parseInt(numTeamsEl.value, 10) : numPlayers;
  const botTeams = botTeamsEl ? parseInt(botTeamsEl.value, 10) : (botsEl ? parseInt(botsEl.value, 10) : 1);
  const humanTeams = numTeams - botTeams;
  const unitsPerTeam = unitsPerTeamEl ? parseInt(unitsPerTeamEl.value, 10) : 1;
  const botUnitsPerTeam = botUnitsEl ? parseInt(botUnitsEl.value, 10) : 1;
  const numBots = botTeams * botUnitsPerTeam;
  const totalPlayers = humanTeams * unitsPerTeam + numBots;
  const turnOrderMode = (turnOrderEl?.value ?? 'sequential') as 'sequential' | 'alternating';
  const friendlyFire = ffEl ? parseInt(ffEl.value, 10) : 0;
  const fallDamageEl = document.getElementById('opt-fall-damage') as HTMLSelectElement | null;
  const fallDamageMultiplier = fallDamageEl ? parseFloat(fallDamageEl.value) : 1.0;
  const datalinkedLOS = datalinkedEl ? datalinkedEl.checked : false;
  const botFogEl = document.getElementById('opt-bot-fog') as HTMLInputElement | null;
  const botFogEnabled = botFogEl ? botFogEl.checked : true;
  const adaptiveEl = document.getElementById('opt-adaptive-personality') as HTMLInputElement | null;
  const adaptiveBotPersonality = adaptiveEl ? adaptiveEl.checked : false;
  const hideEnemyDeployEl = document.getElementById('opt-hide-enemy-deploy') as HTMLInputElement | null;
  const hideEnemyDeploy = hideEnemyDeployEl ? hideEnemyDeployEl.checked : false;
  const hazardsEl = document.getElementById('opt-hazards') as HTMLSelectElement | null;
  const hazardDensity = (hazardsEl?.value ?? 'some') as 'none' | 'some' | 'many';
  const planetSizeEl = document.getElementById('opt-planet-size') as HTMLSelectElement | null;
  const planetSize = (planetSizeEl?.value ?? 'mixed') as PlanetSizePreset;
  const mapLayoutEl = document.getElementById('opt-map-layout') as HTMLSelectElement | null;
  const mapLayout = (mapLayoutEl?.value ?? 'random') as MapLayout;

  const gameModeEl = document.getElementById('opt-game-mode') as HTMLSelectElement | null;
  const turnLimitEl = document.getElementById('opt-turn-limit') as HTMLInputElement | null;

  return {
    numPlayers: totalPlayers,
    numBots,
    numPlanets: planetsEl ? parseInt(planetsEl.value, 10) : 5,
    numStars: starsEl ? parseInt(starsEl.value, 10) : 0,
    numWormholePairs: wormholesEl ? parseInt(wormholesEl.value, 10) : 0,
    seed: seedEl?.value || String(Date.now()),
    playerNames,
    teamIndices,
    cbpBudget: cbpEl ? parseInt(cbpEl.value, 10) : 50,
    playerLoadouts: [],  // populated by shop
    unitSlots: [],        // populated by roster screen
    botDifficulty,
    botPersonality,
    botOverrides,
    numTeams,
    humanTeams,
    botTeams,
    unitsPerTeam,
    botUnitsPerTeam,
    turnOrderMode,
    friendlyFire,
    datalinkedLOS,
    botFogEnabled,
    adaptiveBotPersonality,
    hideEnemyDeploy,
    hazardDensity,
    ruinDensity: 'some',
    floraDensity: 'some',
    planetSize,
    mapLayout,
    starType: ((document.getElementById('opt-star-type') as HTMLSelectElement | null)?.value ?? 'random') as StarType | 'random',
    systemPreset: ((document.getElementById('opt-system-preset') as HTMLSelectElement | null)?.value ?? 'custom') as SystemPreset,
    gameMode: (gameModeEl?.value ?? 'deathmatch') as GameMode,
    modifiers: readModifiers(),
    stealth: (document.getElementById('opt-mod-stealth') as HTMLInputElement | null)?.checked ?? false,
    turnLimit: turnLimitEl && turnLimitEl.value.trim() !== '' ? parseInt(turnLimitEl.value, 10) || undefined : undefined,
    allowedWeapons: readAllowedWeapons(),
    visibility: readVisibility(),
    resolutionPace: readResolutionPace(),
    turnFlow: readTurnFlow(),
    previewQuality: readPreviewQuality(),
    stabilizers: (document.getElementById('opt-stabilizers') as HTMLSelectElement | null)?.value === 'on',
    teamKits: readTeamKits(),
    fallDamageMultiplier,
  };
}

function updateCbpDisplay(): void {
  const cbpEl = document.getElementById('opt-cbp') as HTMLInputElement | null;
  const displayEl = document.getElementById('cbp-display');
  if (cbpEl && displayEl) {
    displayEl.textContent = cbpEl.value;
  }
}

/** Populate company selector dropdowns from TEAMS array and set defaults. */
export function populateCompanySelectors(): void {
  for (let i = 0; i < 4; i++) {
    const sel = document.getElementById(`opt-team-${i}`) as HTMLSelectElement | null;
    if (!sel) continue;
    sel.innerHTML = '';
    for (let t = 0; t < TEAMS.length; t++) {
      const opt = document.createElement('option');
      opt.value = String(t);
      opt.textContent = TEAMS[t].name;
      sel.appendChild(opt);
    }
    // Default: slot i → company i
    sel.value = String(i % TEAMS.length);
    updateCompanySwatch(i);

    // On change: update swatch + name placeholder, sync disabled options
    sel.addEventListener('change', () => {
      updateCompanySwatch(i);
      const teamIdx = parseInt(sel.value, 10);
      const nameEl = document.getElementById(`opt-name-${i}`) as HTMLInputElement | null;
      if (nameEl) nameEl.placeholder = TEAMS[teamIdx].name;
      syncCompanySelectors();
    });
  }
  syncCompanySelectors();
}

/** Disable already-chosen companies in other selectors to prevent duplicates. */
function syncCompanySelectors(): void {
  const selectors: HTMLSelectElement[] = [];
  const chosen = new Set<string>();
  for (let i = 0; i < 4; i++) {
    const sel = document.getElementById(`opt-team-${i}`) as HTMLSelectElement | null;
    if (!sel) continue;
    // Only count visible (active) slots
    const row = document.getElementById(`name-row-${i}`);
    if (row && row.style.display === 'none') continue;
    selectors.push(sel);
    chosen.add(sel.value);
  }
  for (const sel of selectors) {
    const current = sel.value;
    for (const opt of Array.from(sel.options)) {
      // Disable if chosen by another selector (but not this one's own value)
      opt.disabled = opt.value !== current && chosen.has(opt.value);
    }
  }
}

function updateCompanySwatch(slot: number): void {
  const sel = document.getElementById(`opt-team-${slot}`) as HTMLSelectElement | null;
  const swatch = document.getElementById(`company-swatch-${slot}`);
  if (!sel || !swatch) return;
  const teamIdx = parseInt(sel.value, 10);
  swatch.style.backgroundColor = TEAMS[teamIdx].primary;
}

export function updatePlayerNameInputs(): void {
  const numTeamsEl = document.getElementById('opt-num-teams') as HTMLSelectElement | null;
  const botTeamsEl = document.getElementById('opt-bot-teams') as HTMLSelectElement | null;
  const numTeams = numTeamsEl ? parseInt(numTeamsEl.value, 10) : 2;
  const botTeams = botTeamsEl ? parseInt(botTeamsEl.value, 10) : 1;
  const humanTeams = numTeams - botTeams;

  for (let i = 0; i < 4; i++) {
    const row = document.getElementById(`name-row-${i}`);
    if (row) {
      row.style.display = i < numTeams ? 'flex' : 'none';
    }

    // Show per-bot overrides for bot team slots
    const isBot = i >= humanTeams && i < numTeams;
    const diffEl = document.getElementById(`opt-bot-difficulty-${i}`);
    const persEl = document.getElementById(`opt-bot-personality-${i}`);
    if (diffEl) diffEl.style.display = isBot ? 'block' : 'none';
    if (persEl) persEl.style.display = isBot ? 'block' : 'none';
  }
  syncCompanySelectors();
}

/** Fields a system preset owns. Locked (disabled) while a preset is selected. */
const WORLD_FIELD_IDS = ['opt-planets', 'opt-stars', 'opt-star-type', 'opt-wormholes', 'opt-planet-size', 'opt-map-layout', 'opt-hazards'];

function setSystemLock(locked: boolean): void {
  for (const id of WORLD_FIELD_IDS) {
    const el = document.getElementById(id) as HTMLSelectElement | null;
    if (el) el.disabled = locked;
  }
}

function isSystemLocked(): boolean {
  const presetEl = document.getElementById('opt-system-preset') as HTMLSelectElement | null;
  return !!presetEl && presetEl.value !== 'custom';
}

function applySystemPreset(key: SystemPreset): void {
  const preset = SYSTEM_PRESETS[key];
  if (!preset) {
    setSystemLock(false);
    updateSystemDescription('custom');
    return;
  }
  setSystemLock(true);

  // Auto-fill dropdowns
  const setVal = (id: string, val: string) => {
    const el = document.getElementById(id) as HTMLSelectElement | null;
    if (el) el.value = val;
  };

  setVal('opt-star-type', preset.starType);
  setVal('opt-planets', String(preset.numPlanets));
  setVal('opt-stars', String(preset.numStars));
  setVal('opt-wormholes', String(preset.numWormholePairs));
  setVal('opt-planet-size', preset.planetSize);
  setVal('opt-map-layout', preset.mapLayout);
  setVal('opt-hazards', preset.hazardDensity);

  updateSystemDescription(key);
}

function updateSystemDescription(key: SystemPreset): void {
  const descEl = document.getElementById('system-description');
  if (!descEl) return;

  const preset = SYSTEM_PRESETS[key];
  if (preset) {
    descEl.textContent = preset.description;
    descEl.style.display = 'block';
    return;
  }

  // If custom, show star type description instead
  const starTypeEl = document.getElementById('opt-star-type') as HTMLSelectElement | null;
  const st = starTypeEl?.value;
  if (st && st !== 'random' && st in STAR_TYPES) {
    descEl.textContent = STAR_TYPES[st as StarType].description;
    descEl.style.display = 'block';
  } else {
    descEl.textContent = '';
    descEl.style.display = 'none';
  }
}

// =============================================================================
// QUICK-PLAY PRESETS, ADVANCED SECTION, SETUP VALIDATION
// =============================================================================

let activeQuickPreset = 'skirmish';
// Setup defaults (setupRules.ts): a new team is a bot team, bot units follow units/team until set by hand
let prevNumTeams = 2;
let botUnitsTouched = false;

function renderQuickPresets(): void {
  const container = document.getElementById('quick-presets');
  if (!container) return;
  container.innerHTML = '';
  for (const preset of QUICK_PLAY_PRESETS) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'quick-preset';
    btn.dataset.key = preset.key;
    btn.innerHTML = `<div class="qp-name">${preset.name}</div><div class="qp-sub">${preset.blurb}</div>`;
    btn.addEventListener('click', () => {
      if (preset.key === 'custom') {
        markSetupCustom();
        setAdvancedOpen(true);
      } else {
        applyQuickPreset(preset.key);
      }
    });
    container.appendChild(btn);
  }
  highlightQuickPreset();
}

function highlightQuickPreset(): void {
  for (const el of Array.from(document.querySelectorAll<HTMLElement>('.quick-preset'))) {
    el.classList.toggle('active', el.dataset.key === activeQuickPreset);
  }
}

/** Fill every setup field from a quick-play preset. */
function applyQuickPreset(key: string): void {
  const preset = QUICK_PLAY_PRESETS.find(p => p.key === key);
  if (!preset) return;
  activeQuickPreset = key;
  for (const [id, value] of Object.entries(preset.fields)) {
    const el = document.getElementById(id) as HTMLInputElement | HTMLSelectElement | null;
    if (!el) continue;
    if (el instanceof HTMLInputElement && el.type === 'checkbox') el.checked = value === true;
    else el.value = String(value);
  }
  const system = (preset.fields['opt-system-preset'] as SystemPreset | undefined) ?? 'custom';
  applySystemPreset(system);
  prevNumTeams = parseInt(String(preset.fields['opt-num-teams'] ?? '2'), 10) || 2;
  botUnitsTouched = false;   // a preset sets both unit counts; Bot Units follows Units/Team again
  clampBotTeams();
  updatePlayerNameInputs();
  updateCbpDisplay();
  updateModeDescription(((preset.fields['opt-game-mode'] as string | undefined) ?? 'deathmatch') as GameMode);
  resetKitGrid();
  runSetupValidation();
  highlightQuickPreset();
}

/** Any hand edit under Advanced turns the setup into "Custom". */
function markSetupCustom(): void {
  if (activeQuickPreset === 'custom') return;
  activeQuickPreset = 'custom';
  highlightQuickPreset();
}

function setAdvancedOpen(open: boolean): void {
  const section = document.getElementById('advanced-section');
  const icon = document.getElementById('advanced-toggle-icon');
  if (section) section.style.display = open ? 'block' : 'none';
  if (icon) icon.innerHTML = open ? '&#9660; HIDE' : '&#9654; SHOW';
}

function isAdvancedOpen(): boolean {
  const section = document.getElementById('advanced-section');
  return !!section && section.style.display !== 'none';
}

/**
 * Default behaviours when a team or unit control changes (setupRules.ts):
 *   Teams      → the bot-team count absorbs the change, human teams stay (a new team is a bot team)
 *   Units/Team → Bot Units follows until the player has set Bot Units by hand
 *   Bot Units  → remembered as hand-set
 */
function applySetupDefaults(changedId: string): void {
  const sel = (id: string) => document.getElementById(id) as HTMLSelectElement | null;
  if (changedId === 'opt-num-teams') {
    const numTeamsEl = sel('opt-num-teams'), botTeamsEl = sel('opt-bot-teams');
    if (numTeamsEl && botTeamsEl) {
      const newTeams = parseInt(numTeamsEl.value, 10);
      const prevBots = parseInt(botTeamsEl.value, 10);
      botTeamsEl.value = String(botTeamsAfterTeamCountChange(prevNumTeams, prevBots, newTeams));
      prevNumTeams = newTeams;
    }
    clampBotTeams();
    updatePlayerNameInputs();
  } else if (changedId === 'opt-units-per-team') {
    const unitsEl = sel('opt-units-per-team'), botUnitsEl = sel('opt-bot-units');
    if (unitsEl && botUnitsEl) botUnitsEl.value = String(botUnitsAfterUnitsChange(parseInt(unitsEl.value, 10), parseInt(botUnitsEl.value, 10), botUnitsTouched));
  } else if (changedId === 'opt-bot-units') {
    botUnitsTouched = true;
  }
}

/** Bot teams can never exceed teams: disable the impossible options and clamp the value. */
function clampBotTeams(): void {
  const numTeamsEl = document.getElementById('opt-num-teams') as HTMLSelectElement | null;
  const botTeamsEl = document.getElementById('opt-bot-teams') as HTMLSelectElement | null;
  if (!numTeamsEl || !botTeamsEl) return;
  const numTeams = parseInt(numTeamsEl.value, 10);
  for (const opt of Array.from(botTeamsEl.options)) opt.disabled = parseInt(opt.value, 10) > numTeams;
  if (parseInt(botTeamsEl.value, 10) > numTeams) botTeamsEl.value = String(numTeams);
}

function currentSetupWarnings(): ConfigWarning[] {
  const val = (id: string, def: string) => (document.getElementById(id) as HTMLSelectElement | null)?.value ?? def;
  const mode = val('opt-game-mode', 'deathmatch') as GameMode;
  const numTeams = parseInt(val('opt-num-teams', '2'), 10);
  const botTeams = parseInt(val('opt-bot-teams', '1'), 10);
  const numPlanets = parseInt(val('opt-planets', '5'), 10);
  const planetSize = val('opt-planet-size', 'mixed');
  return [...validateTeamSetup(numTeams, botTeams, planetSize, numPlanets), ...validateModeConfig(composeModeConfig(mode, { modifiers: readModifiers() }), numTeams, numPlanets, 'some')];
}

// =============================================================================
// KIT GRID (dev) — per-team launcher / suit / trait tiers for quick play
// =============================================================================

const KIT_LAUNCHER: LauncherModKey[] = ['preview', 'range', 'yield', 'blast', 'probe'];
const KIT_SUIT: SuitModKey[] = ['armor', 'medkit', 'padding', 'thrusters', 'servos', 'camo', 'sensors'];
const KIT_TRAITS: Array<'fitness' | 'jump'> = ['fitness', 'jump'];
const KIT_TEAMS = 4;

function kitInputId(team: number, field: string): string { return `kit-${team}-${field}`; }

function renderKitGrid(): void {
  const el = document.getElementById('kit-grid');
  if (!el) return;
  const head = (labels: string[]) => labels.map(l => `<th style="padding:2px 4px; opacity:0.5; font-weight:500; text-align:center;">${l}</th>`).join('');
  const cell = (team: number, field: string) =>
    `<td style="padding:1px 2px; text-align:center;"><input type="number" min="0" max="3" value="0" id="${kitInputId(team, field)}" class="menu-input" style="width:34px; padding:2px 2px; text-align:center;" /></td>`;
  let html = '<table style="border-collapse:collapse;"><thead><tr><th></th>'
    + `<th colspan="${KIT_LAUNCHER.length}" style="padding:2px; opacity:0.7; letter-spacing:1px;">LAUNCHER</th>`
    + `<th colspan="${KIT_SUIT.length}" style="padding:2px; opacity:0.7; letter-spacing:1px;">SUIT</th>`
    + `<th colspan="${KIT_TRAITS.length}" style="padding:2px; opacity:0.7; letter-spacing:1px;">TRAITS</th></tr>`
    + `<tr><th style="opacity:0.5; text-align:left; padding:2px 4px;">team</th>${head(KIT_LAUNCHER)}${head(KIT_SUIT)}${head(KIT_TRAITS)}</tr></thead><tbody>`;
  for (let t = 0; t < KIT_TEAMS; t++) {
    html += `<tr><td style="padding:2px 6px; opacity:0.6;">${t + 1}</td>`
      + KIT_LAUNCHER.map(f => cell(t, f)).join('') + KIT_SUIT.map(f => cell(t, f)).join('') + KIT_TRAITS.map(f => cell(t, f)).join('')
      + '</tr>';
  }
  html += '</tbody></table>';
  el.innerHTML = html;
}

/** 37.20-next / setup kit picker: one preset select per team (player-facing); the dev grid lays tiers on top of it. */
function kitPresetId(team: number): string { return `kit-preset-${team}`; }

function renderKitPresets(): void {
  const el = document.getElementById('kit-presets');
  if (!el) return;
  const keys = Object.keys(KIT_PRESETS) as KitPresetKey[];
  const options = keys.map(k => `<option value="${k}" title="${KIT_PRESET_INFO[k].blurb}">${KIT_PRESET_INFO[k].name}</option>`).join('');
  el.innerHTML = Array.from({ length: KIT_TEAMS }, (_, t) =>
    `<label style="display:inline-flex;align-items:center;gap:4px;margin-right:12px;font-size:11px;">Team ${t + 1}`
    + `<select class="menu-input" id="${kitPresetId(t)}" style="width:110px;">${options}</select></label>`).join('');
  const blurb = document.getElementById('kit-preset-blurb');
  for (let t = 0; t < KIT_TEAMS; t++) {
    const sel = document.getElementById(kitPresetId(t)) as HTMLSelectElement | null;
    sel?.addEventListener('change', () => {
      const key = sel.value as KitPresetKey;
      if (blurb) blurb.textContent = KIT_PRESET_INFO[key]?.blurb ?? '';
      // Mirror the preset's tiers into the dev grid row so a dev can see and tweak them
      const k = KIT_PRESETS[key];
      for (const f of KIT_LAUNCHER) { const c = document.getElementById(kitInputId(t, f)) as HTMLInputElement | null; if (c) c.value = String(k?.launcher[f] ?? 0); }
      for (const f of KIT_SUIT) { const c = document.getElementById(kitInputId(t, f)) as HTMLInputElement | null; if (c) c.value = String(k?.suit[f] ?? 0); }
      for (const f of KIT_TRAITS) { const c = document.getElementById(kitInputId(t, f)) as HTMLInputElement | null; if (c) c.value = String(k?.traits[f] ?? 0); }
    });
  }
}

function readKitPreset(team: number): KitPresetKey {
  const sel = document.getElementById(kitPresetId(team)) as HTMLSelectElement | null;
  const v = sel?.value as KitPresetKey | undefined;
  return v && v in KIT_PRESETS ? v : 'standard';
}

function resetKitGrid(): void {
  for (let t = 0; t < KIT_TEAMS; t++) {
    const sel = document.getElementById(kitPresetId(t)) as HTMLSelectElement | null;
    if (sel) sel.value = 'standard';
  }
  for (let t = 0; t < KIT_TEAMS; t++) {
    for (const f of [...KIT_LAUNCHER, ...KIT_SUIT, ...KIT_TRAITS]) {
      const el = document.getElementById(kitInputId(t, f)) as HTMLInputElement | null;
      if (el) el.value = '0';
    }
  }
}

/**
 * A team's kit = its chosen preset with the dev grid's non-zero tiers laid on top. Teams on Standard with an
 * untouched grid row keep the default; undefined when nobody chose anything (today's game).
 */
function readTeamKits(): Partial<Record<number, UnitKit>> | undefined {
  const out: Partial<Record<number, UnitKit>> = {};
  let any = false;
  const tier = (team: number, f: string) => {
    const el = document.getElementById(kitInputId(team, f)) as HTMLInputElement | null;
    const v = el ? parseInt(el.value, 10) : 0;
    return Number.isFinite(v) ? Math.max(0, Math.min(3, v)) : 0;
  };
  for (let t = 0; t < KIT_TEAMS; t++) {
    const preset = readKitPreset(t);
    const tiers = {
      launcher: Object.fromEntries(KIT_LAUNCHER.map(f => [f, tier(t, f)])),
      suit: Object.fromEntries(KIT_SUIT.map(f => [f, tier(t, f)])),
      traits: Object.fromEntries(KIT_TRAITS.map(f => [f, tier(t, f)])),
    };
    const nonZero = [...Object.values(tiers.launcher), ...Object.values(tiers.suit), ...Object.values(tiers.traits)].some(v => v > 0);
    if (preset === 'standard' && !nonZero) continue;
    out[t] = applyKitTiers(kitPreset(preset), tiers);
    any = true;
  }
  return any ? out : undefined;
}

function showMainMenu(): void {
  hideAllScreens();
  const mainMenu = document.getElementById('main-menu');
  if (mainMenu) mainMenu.style.display = 'block';
}

function showQuickmatchConfig(): void {
  hideAllScreens();
  const quickmatchConfig = document.getElementById('quickmatch-config');
  if (quickmatchConfig) quickmatchConfig.style.display = 'block';
}

function showLeaderboard(): void {
  hideAllScreens();
  const leaderboard = document.getElementById('leaderboard-screen');
  if (leaderboard) leaderboard.style.display = 'block';
}

function showLab(): void {
  hideAllScreens();
  const lab = document.getElementById('lab-screen');
  if (lab) lab.style.display = 'block';
}

function showSettings(): void {
  hideAllScreens();
  const settings = document.getElementById('settings-screen');
  if (settings) settings.style.display = 'block';
}

function showHelp(): void {
  hideAllScreens();
  const help = document.getElementById('help-screen');
  if (help) help.style.display = 'block';
}

function showShop(): void {
  hideAllScreens();
  const shop = document.getElementById('shop-screen');
  if (shop) shop.style.display = 'block';
}

function showRoster(): void {
  hideAllScreens();
  const roster = document.getElementById('roster-screen');
  if (roster) roster.style.display = 'block';
}

function showP2PLobby(): void {
  hideAllScreens();
  const lobby = document.getElementById('p2p-lobby');
  if (lobby) lobby.style.display = 'block';
}

export function updateP2PStatus(text: string, cls?: 'net-connected' | 'net-waiting'): void {
  const el = document.getElementById('p2p-status');
  if (!el) return;
  el.textContent = text;
  el.className = cls || '';
}

/** Show quickmatch config for the host to configure, then start. */
export function showP2PHostConfig(): void {
  showQuickmatchConfig();
}

/** Hide menu overlay entirely (used when client receives config and game starts). */
export function hideMenuOverlay(): void {
  const overlay = document.getElementById('menu-overlay');
  if (overlay) overlay.style.display = 'none';
}

// =============================================================================
// ROSTER STATE
// =============================================================================

const UNIT_SUFFIX_LABELS = ['Alpha', 'Bravo', 'Charlie'];

function buildRosterUI(options: MenuOptions): void {
  const container = document.getElementById('roster-teams');
  if (!container) return;

  const numTeams = options.numTeams;
  const humanTeams = options.humanTeams;
  const maxUnits = Math.max(options.unitsPerTeam, options.botUnitsPerTeam);

  // Build default unitSlots if not already set
  options.unitSlots = [];
  for (let t = 0; t < numTeams; t++) {
    const isBot = t >= humanTeams;
    const upt = isBot ? options.botUnitsPerTeam : options.unitsPerTeam;
    const slots: UnitSlotConfig[] = [];
    for (let u = 0; u < maxUnits; u++) {
      if (u < upt) {
        slots.push({
          type: isBot ? 'bot' : 'player',
          name: '',
          difficulty: options.botDifficulty,
          personality: options.botPersonality,
        });
      } else {
        slots.push({
          type: 'none',
          name: '',
          difficulty: options.botDifficulty,
          personality: options.botPersonality,
        });
      }
    }
    options.unitSlots.push(slots);
  }

  renderRoster(options);
}

function renderRoster(options: MenuOptions): void {
  const container = document.getElementById('roster-teams');
  if (!container) return;

  const numTeams = options.numTeams;
  const maxUnits = options.unitSlots[0]?.length ?? 1;

  container.innerHTML = '';

  for (let t = 0; t < numTeams; t++) {
    const teamIdx = options.teamIndices[t] ?? t % TEAMS.length;
    const team = TEAMS[teamIdx];
    const teamName = options.playerNames[t]?.trim() || team.name;

    // Team header
    const header = document.createElement('div');
    header.style.cssText = `display:flex; align-items:center; gap:8px; margin:16px 0 8px 0; padding-bottom:6px; border-bottom:1px solid ${theme.sectionDivider};`;
    header.innerHTML = `
      <span style="font-family:'Space Grotesk',sans-serif; font-size:12px; font-weight:600; letter-spacing:1.5px; opacity:0.6;">TEAM ${t + 1}: ${teamName.toUpperCase()}</span>
      <span style="display:inline-block; width:12px; height:12px; border-radius:3px; background:${team.primary}; border:1px solid rgba(255,255,255,0.1);"></span>
    `;
    container.appendChild(header);

    // Unit rows
    for (let u = 0; u < maxUnits; u++) {
      const slot = options.unitSlots[t][u];
      const row = document.createElement('div');
      row.style.cssText = `display:flex; align-items:center; gap:8px; padding:6px 8px; margin-bottom:4px; background:${theme.unitSlotBg}; border-radius:6px;`;
      row.dataset.team = String(t);
      row.dataset.unit = String(u);

      const suffixLabel = UNIT_SUFFIX_LABELS[u] ?? `Unit ${u + 1}`;

      // Suffix label
      const labelSpan = document.createElement('span');
      labelSpan.style.cssText = 'font-family:"JetBrains Mono",monospace; font-size:11px; opacity:0.5; min-width:55px;';
      labelSpan.textContent = suffixLabel;
      row.appendChild(labelSpan);

      // Type dropdown
      const typeSelect = document.createElement('select');
      typeSelect.className = 'menu-input';
      typeSelect.style.cssText = 'flex:0 0 80px; font-size:11px; padding:4px 6px;';
      typeSelect.dataset.team = String(t);
      typeSelect.dataset.unit = String(u);
      typeSelect.dataset.field = 'type';
      for (const val of ['player', 'bot', 'none'] as const) {
        const opt = document.createElement('option');
        opt.value = val;
        opt.textContent = val === 'player' ? 'Player' : val === 'bot' ? 'Bot' : 'None';
        if (val === slot.type) opt.selected = true;
        typeSelect.appendChild(opt);
      }
      typeSelect.addEventListener('change', () => {
        const newType = typeSelect.value as 'player' | 'bot' | 'none';
        options.unitSlots[t][u] = { ...options.unitSlots[t][u], type: newType };
        renderRoster(options);
      });
      row.appendChild(typeSelect);

      if (slot.type !== 'none') {
        // Name input
        const nameInput = document.createElement('input');
        nameInput.className = 'menu-input';
        nameInput.type = 'text';
        nameInput.maxLength = 20;
        nameInput.placeholder = teamName;
        nameInput.value = slot.name;
        nameInput.style.cssText = 'flex:1; font-size:11px; padding:4px 6px; min-width:0;';
        nameInput.dataset.team = String(t);
        nameInput.dataset.unit = String(u);
        nameInput.dataset.field = 'name';
        nameInput.addEventListener('input', () => {
          options.unitSlots[t][u] = { ...options.unitSlots[t][u], name: nameInput.value };
        });
        row.appendChild(nameInput);
      }

      if (slot.type === 'bot') {
        // Difficulty dropdown
        const diffSelect = document.createElement('select');
        diffSelect.className = 'menu-input';
        diffSelect.style.cssText = 'flex:0 0 75px; font-size:11px; padding:4px 6px;';
        diffSelect.dataset.team = String(t);
        diffSelect.dataset.unit = String(u);
        diffSelect.dataset.field = 'difficulty';
        for (const val of ['easy', 'medium', 'hard', 'expert']) {
          const opt = document.createElement('option');
          opt.value = val;
          opt.textContent = val.charAt(0).toUpperCase() + val.slice(1);
          if (val === slot.difficulty) opt.selected = true;
          diffSelect.appendChild(opt);
        }
        diffSelect.addEventListener('change', () => {
          options.unitSlots[t][u] = { ...options.unitSlots[t][u], difficulty: diffSelect.value };
        });
        row.appendChild(diffSelect);

        // Personality dropdown
        const persSelect = document.createElement('select');
        persSelect.className = 'menu-input';
        persSelect.style.cssText = 'flex:0 0 90px; font-size:11px; padding:4px 6px;';
        persSelect.dataset.team = String(t);
        persSelect.dataset.unit = String(u);
        persSelect.dataset.field = 'personality';
        for (const val of ['balanced', 'aggressive', 'defensive', 'chaotic']) {
          const opt = document.createElement('option');
          opt.value = val;
          opt.textContent = val.charAt(0).toUpperCase() + val.slice(1);
          if (val === slot.personality) opt.selected = true;
          persSelect.appendChild(opt);
        }
        persSelect.addEventListener('change', () => {
          options.unitSlots[t][u] = { ...options.unitSlots[t][u], personality: persSelect.value };
        });
        row.appendChild(persSelect);
      }

      container.appendChild(row);
    }
  }
}

function getRosterConfig(options: MenuOptions): { unitSlots: UnitSlotConfig[][]; numPlayers: number; numBots: number } {
  // unitSlots are already live-updated via event handlers, just compute counts
  let numPlayers = 0;
  let numBots = 0;
  for (const teamSlots of options.unitSlots) {
    for (const slot of teamSlots) {
      if (slot.type === 'player') numPlayers++;
      else if (slot.type === 'bot') numBots++;
    }
  }
  return { unitSlots: options.unitSlots, numPlayers: numPlayers + numBots, numBots };
}

// =============================================================================
// SHOP STATE
// =============================================================================

interface ShopState {
  options: MenuOptions;
  currentPlayer: number;
  playerBudgets: number[];  // remaining CBP per player
  playerAmmo: Array<Partial<Record<WeaponKey, number>>>;  // purchased ammo per player
  playerFuel: number[];  // fuel purchased per team
  playerItems: Array<Partial<Record<ItemKey, number>>>;  // purchased heal items per player
}

let shopState: ShopState | null = null;

// Purchasable weapons (exclude standard which is unlimited/free)
const PURCHASABLE_WEAPONS: WeaponKey[] = ['heavy', 'cluster', 'nuke', 'probe', 'punch', 'antigrav', 'gravBomb', 'tether', 'terraformer', 'emp', 'drill', 'fire'];

function initShopState(options: MenuOptions): void {
  // Shop operates per-team with ONE unit's worth of credits; the purchased kit is
  // issued to every unit on the team. (A per-unit purchase screen for local
  // multiplayer / specialised squads is a possible future option — BUILDLIST 37.x.)
  const numTeams = options.numTeams ?? options.numPlayers;
  const budgets: number[] = Array(numTeams).fill(options.cbpBudget);
  shopState = {
    options,
    currentPlayer: 0, // actually current team index
    playerBudgets: budgets,
    playerAmmo: Array(numTeams).fill(null).map(() => {
      const ammo: Partial<Record<WeaponKey, number>> = {};
      const allowed = options.allowedWeapons;
      const keys = allowed
        ? PURCHASABLE_WEAPONS.filter(k => allowed.includes(k))
        : PURCHASABLE_WEAPONS;
      for (const k of keys) ammo[k] = 0;
      return ammo;
    }),
    playerFuel: Array(numTeams).fill(0),
    playerItems: Array(numTeams).fill(null).map(() => ({ healSmall: 0, healLarge: 0 })),
  };
}

function getHumanTeamIndices(): number[] {
  if (!shopState) return [];
  const opts = shopState.options;
  if (opts.unitSlots.length > 0) {
    // A team has human units if any slot is 'player'
    const indices: number[] = [];
    for (let t = 0; t < opts.unitSlots.length; t++) {
      if (opts.unitSlots[t].some(s => s.type === 'player')) {
        indices.push(t);
      }
    }
    return indices;
  }
  // Fallback: first humanTeams teams
  const humanTeams = opts.humanTeams ?? (opts.numPlayers - opts.numBots);
  const indices: number[] = [];
  for (let i = 0; i < humanTeams; i++) indices.push(i);
  return indices;
}

function renderShopTabs(): void {
  if (!shopState) return;
  const tabsEl = document.getElementById('shop-player-tabs');
  if (!tabsEl) return;

  const humanIndices = getHumanTeamIndices();
  tabsEl.innerHTML = '';
  for (const i of humanIndices) {
    const tab = document.createElement('button');
    const teamIdx = shopState.options.teamIndices[i] ?? i % TEAMS.length;
    const playerName = shopState.options.playerNames[i] || TEAMS[teamIdx].name;
    tab.textContent = playerName.length > 12 ? playerName.slice(0, 12) + '…' : playerName;
    tab.className = i === shopState.currentPlayer ? 'menu-btn primary' : 'menu-btn';
    tab.style.flex = '1';
    tab.style.padding = '8px';
    tab.style.fontSize = '11px';
    tab.onclick = () => {
      if (shopState) {
        shopState.currentPlayer = i;
        renderShopTabs();
        renderShopItems();
        updateShopRemaining();
      }
    };
    tabsEl.appendChild(tab);
  }
}

function renderShopItems(): void {
  if (!shopState) return;
  const itemsEl = document.getElementById('shop-items');
  if (!itemsEl) return;

  const playerAmmo = shopState.playerAmmo[shopState.currentPlayer];
  const budget = shopState.playerBudgets[shopState.currentPlayer];

  const allowed = shopState.options.allowedWeapons;
  const shopWeapons = allowed
    ? PURCHASABLE_WEAPONS.filter(k => allowed.includes(k))
    : PURCHASABLE_WEAPONS;

  itemsEl.innerHTML = '';
  for (const key of shopWeapons) {
    const weapon = WEAPONS[key];
    const count = playerAmmo[key] || 0;
    const canAfford = budget >= weapon.cbpCost;

    const row = document.createElement('div');
    row.style.cssText = `display:flex; align-items:center; gap:12px; padding:10px 12px; background:${theme.rowBg}; border-radius:8px;`;

    row.innerHTML = `
      <span style="font-size:18px; width:24px; text-align:center;">${weapon.icon}</span>
      <div style="flex:1;">
        <div style="font-family:'Space Grotesk',sans-serif; font-size:13px; font-weight:500;">${weaponDisplayName(key)}</div>
        <div style="font-family:'JetBrains Mono',monospace; font-size:10px; opacity:0.4;">${weapon.cbpCost} ${cur()} each (${weapon.cbpCost * DELIVERY_COST_MULTIPLIER} by drone) • DMG ${weapon.damage} • R ${weapon.blastRadius.toFixed(1)}</div>
      </div>
      <div style="display:flex; align-items:center; gap:8px;">
        <button class="shop-btn minus" data-weapon="${key}" style="width:28px; height:28px; border:1px solid ${theme.btnMinus.border}; background:${theme.btnMinus.bg}; border-radius:4px; color:${theme.btnMinus.text}; cursor:pointer; font-size:16px;">−</button>
        <span style="font-family:'JetBrains Mono',monospace; font-size:16px; min-width:24px; text-align:center;">${count}</span>
        <button class="shop-btn plus" data-weapon="${key}" ${!canAfford ? 'disabled' : ''} style="width:28px; height:28px; border:1px solid ${canAfford ? theme.btnPositive.border : 'rgba(255,255,255,0.1)'}; background:${canAfford ? theme.btnPositive.bg : theme.unitSlotBg}; border-radius:4px; color:${canAfford ? theme.btnPositive.text : theme.btnDisabled.text}; cursor:${canAfford ? 'pointer' : 'not-allowed'}; font-size:16px;">+</button>
      </div>
    `;

    itemsEl.appendChild(row);
  }

  // Fuel row
  const fuelCount = shopState.playerFuel[shopState.currentPlayer];
  const canAffordFuel = budget >= JUMPPACK_FUEL_COST;
  const fuelRow = document.createElement('div');
  fuelRow.style.cssText = `grid-column:1/-1; display:flex; align-items:center; gap:12px; padding:10px 12px; background:${theme.rowBg}; border-radius:8px;`;
  fuelRow.innerHTML = `
    <span style="font-size:18px; width:24px; text-align:center;">&#x26A1;</span>
    <div style="flex:1;">
      <div style="font-family:'Space Grotesk',sans-serif; font-size:13px; font-weight:500;">Jumppack Fuel</div>
      <div style="font-family:'JetBrains Mono',monospace; font-size:10px; opacity:0.4;">${JUMPPACK_FUEL_COST} ${cur()} each (${JUMPPACK_FUEL_COST * DELIVERY_COST_MULTIPLIER} by drone) • +15 velocity per jump</div>
    </div>
    <div style="display:flex; align-items:center; gap:8px;">
      <button class="fuel-btn minus" style="width:28px; height:28px; border:1px solid ${theme.btnMinus.border}; background:${theme.btnMinus.bg}; border-radius:4px; color:${theme.btnMinus.text}; cursor:pointer; font-size:16px;">−</button>
      <span style="font-family:'JetBrains Mono',monospace; font-size:16px; min-width:24px; text-align:center;">${fuelCount}</span>
      <button class="fuel-btn plus" ${!canAffordFuel ? 'disabled' : ''} style="width:28px; height:28px; border:1px solid ${canAffordFuel ? theme.btnPositive.border : 'rgba(255,255,255,0.1)'}; background:${canAffordFuel ? theme.btnPositive.bg : theme.unitSlotBg}; border-radius:4px; color:${canAffordFuel ? theme.btnPositive.text : theme.btnDisabled.text}; cursor:${canAffordFuel ? 'pointer' : 'not-allowed'}; font-size:16px;">+</button>
    </div>
  `;
  itemsEl.appendChild(fuelRow);

  // Heal item rows
  for (const itemKey of ['healSmall', 'healLarge'] as ItemKey[]) {
    const item = HEAL_ITEMS[itemKey];
    const itemCount = shopState.playerItems[shopState.currentPlayer][itemKey] || 0;
    const canAffordItem = budget >= item.cbpCost;
    const itemRow = document.createElement('div');
    itemRow.style.cssText = `display:flex; align-items:center; gap:12px; padding:10px 12px; background:${theme.healRowBg}; border-radius:8px;`;
    itemRow.innerHTML = `
      <span style="font-size:18px; width:24px; text-align:center;">${itemKey === 'healSmall' ? '✚' : '✛'}</span>
      <div style="flex:1;">
        <div style="font-family:'Space Grotesk',sans-serif; font-size:13px; font-weight:500; color:${theme.heal};">${itemDisplayName(itemKey)}</div>
        <div style="font-family:'JetBrains Mono',monospace; font-size:10px; opacity:0.4;">${item.cbpCost} ${cur()} each (${Math.ceil(item.cbpCost * DELIVERY_COST_MULTIPLIER)} by drone) • +${item.healPercent}% HP</div>
      </div>
      <div style="display:flex; align-items:center; gap:8px;">
        <button class="heal-shop-btn minus" data-item="${itemKey}" style="width:28px; height:28px; border:1px solid ${theme.btnMinus.border}; background:${theme.btnMinus.bg}; border-radius:4px; color:${theme.btnMinus.text}; cursor:pointer; font-size:16px;">−</button>
        <span style="font-family:'JetBrains Mono',monospace; font-size:16px; min-width:24px; text-align:center;">${itemCount}</span>
        <button class="heal-shop-btn plus" data-item="${itemKey}" ${!canAffordItem ? 'disabled' : ''} style="width:28px; height:28px; border:1px solid ${canAffordItem ? theme.btnHeal.border : 'rgba(255,255,255,0.1)'}; background:${canAffordItem ? theme.btnHeal.bg : theme.unitSlotBg}; border-radius:4px; color:${canAffordItem ? theme.btnHeal.text : theme.btnDisabled.text}; cursor:${canAffordItem ? 'pointer' : 'not-allowed'}; font-size:16px;">+</button>
      </div>
    `;
    itemsEl.appendChild(itemRow);
  }

  // Add click handlers
  itemsEl.querySelectorAll('.shop-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const target = e.currentTarget as HTMLButtonElement;
      const weaponKey = target.dataset.weapon as WeaponKey;
      const isPlus = target.classList.contains('plus');
      handleShopAdjust(weaponKey, isPlus ? 1 : -1);
    });
  });

  // Fuel click handlers
  itemsEl.querySelectorAll('.fuel-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const target = e.currentTarget as HTMLButtonElement;
      const isPlus = target.classList.contains('plus');
      handleFuelAdjust(isPlus ? 1 : -1);
    });
  });

  // Heal item click handlers
  itemsEl.querySelectorAll('.heal-shop-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const target = e.currentTarget as HTMLButtonElement;
      const itemKey = target.dataset.item as ItemKey;
      const isPlus = target.classList.contains('plus');
      handleHealItemAdjust(itemKey, isPlus ? 1 : -1);
    });
  });
}

function handleShopAdjust(weaponKey: WeaponKey, delta: number): void {
  if (!shopState) return;
  const playerIdx = shopState.currentPlayer;
  const weapon = WEAPONS[weaponKey];
  const currentCount = shopState.playerAmmo[playerIdx][weaponKey] || 0;
  const newCount = Math.max(0, currentCount + delta);

  if (delta > 0 && shopState.playerBudgets[playerIdx] < weapon.cbpCost) {
    return; // Can't afford
  }

  const costDelta = (newCount - currentCount) * weapon.cbpCost;
  shopState.playerAmmo[playerIdx][weaponKey] = newCount;
  shopState.playerBudgets[playerIdx] -= costDelta;

  renderShopItems();
  updateShopRemaining();
}

function handleFuelAdjust(delta: number): void {
  if (!shopState) return;
  const playerIdx = shopState.currentPlayer;
  const currentFuel = shopState.playerFuel[playerIdx];
  const newFuel = Math.max(0, currentFuel + delta);

  if (delta > 0 && shopState.playerBudgets[playerIdx] < JUMPPACK_FUEL_COST) {
    return; // Can't afford
  }

  const costDelta = (newFuel - currentFuel) * JUMPPACK_FUEL_COST;
  shopState.playerFuel[playerIdx] = newFuel;
  shopState.playerBudgets[playerIdx] -= costDelta;

  renderShopItems();
  updateShopRemaining();
}

function handleHealItemAdjust(itemKey: ItemKey, delta: number): void {
  if (!shopState) return;
  const playerIdx = shopState.currentPlayer;
  const item = HEAL_ITEMS[itemKey];
  const currentCount = shopState.playerItems[playerIdx][itemKey] || 0;
  const newCount = Math.max(0, currentCount + delta);

  if (delta > 0 && shopState.playerBudgets[playerIdx] < item.cbpCost) {
    return; // Can't afford
  }

  const costDelta = (newCount - currentCount) * item.cbpCost;
  shopState.playerItems[playerIdx][itemKey] = newCount;
  shopState.playerBudgets[playerIdx] -= costDelta;

  renderShopItems();
  updateShopRemaining();
}

function updateShopRemaining(): void {
  if (!shopState) return;
  const el = document.getElementById('shop-remaining');
  if (el) {
    el.textContent = String(shopState.playerBudgets[shopState.currentPlayer]);
  }
  const note = document.getElementById('shop-carry-note');
  if (note) {
    note.textContent = `Unspent ${cur()} carry into the match as each unit's starting ${cur()}. Mid-match drone delivery costs ${DELIVERY_COST_MULTIPLIER}× the shop price per item.`;
  }
}

/** What a bot's auto-loadout cost, so its leftover carries into the match like a human's. */
function botLoadoutSpend(result: { purchases: Partial<Record<WeaponKey, number>>; fuelPurchased: number }): number {
  let spent = result.fuelPurchased * JUMPPACK_FUEL_COST;
  for (const [key, count] of Object.entries(result.purchases) as Array<[WeaponKey, number | undefined]>) {
    spent += (count ?? 0) * WEAPONS[key].cbpCost;
  }
  return spent;
}

function getShopLoadouts(): PlayerLoadout[] {
  if (!shopState) return [];

  return shopState.playerAmmo.map((ammo, idx) => {
    const items = shopState!.playerItems[idx];
    const healSmall = items.healSmall || 0;
    const healLarge = items.healLarge || 0;
    return {
      ammo: {
        standard: null,  // unlimited
        heavy: ammo.heavy || 0,
        cluster: ammo.cluster || 0,
        nuke: ammo.nuke || 0,
        probe: ammo.probe || 0,
        punch: ammo.punch || 0,
        antigrav: ammo.antigrav || 0,
        gravBomb: ammo.gravBomb || 0,
        tether: ammo.tether || 0,
        terraformer: ammo.terraformer || 0,
        emp: ammo.emp || 0,
        drill: ammo.drill || 0,
        fire: ammo.fire || 0,
      },
      upgrades: {},
      items: (healSmall > 0 || healLarge > 0) ? { healSmall, healLarge } : undefined,
    };
  });
}

function hideAllScreens(): void {
  const screens = ['main-menu', 'quickmatch-config', 'leaderboard-screen', 'settings-screen', 'lab-screen', 'help-screen', 'shop-screen', 'roster-screen', 'p2p-lobby'];
  for (const id of screens) {
    const el = document.getElementById(id);
    if (el) el.style.display = 'none';
  }
}

function initArmoryUI(): void {
  const container = document.getElementById('armory-weapons');
  if (!container) return;

  container.innerHTML = '';
  for (const key of PURCHASABLE_WEAPONS) {
    const w = WEAPONS[key];
    const label = document.createElement('label');
    label.style.cssText = 'display:flex; align-items:center; gap:4px; cursor:pointer; padding:2px 0;';
    label.innerHTML = `<input type="checkbox" id="opt-armory-${key}" checked style="cursor:pointer;"/>` +
      `<span style="width:16px; text-align:center;">${w.icon}</span>` +
      `<span style="flex:1; opacity:0.8;">${weaponDisplayName(key)}</span>` +
      `<span style="font-family:'JetBrains Mono',monospace; opacity:0.35;">${w.cbpCost}</span>`;
    container.appendChild(label);
  }

  // Toggle
  const toggle = document.getElementById('armory-toggle');
  const section = document.getElementById('armory-section');
  const icon = document.getElementById('armory-toggle-icon');
  if (toggle && section && icon) {
    toggle.onclick = () => {
      const visible = section.style.display !== 'none';
      section.style.display = visible ? 'none' : 'block';
      icon.textContent = visible ? '▶ SHOW' : '▼ HIDE';
    };
  }

  // All / None buttons
  const allBtn = document.getElementById('armory-all');
  const noneBtn = document.getElementById('armory-none');
  if (allBtn) {
    allBtn.onclick = () => {
      for (const key of PURCHASABLE_WEAPONS) {
        const cb = document.getElementById(`opt-armory-${key}`) as HTMLInputElement | null;
        if (cb) cb.checked = true;
      }
    };
  }
  if (noneBtn) {
    noneBtn.onclick = () => {
      for (const key of PURCHASABLE_WEAPONS) {
        const cb = document.getElementById(`opt-armory-${key}`) as HTMLInputElement | null;
        if (cb) cb.checked = false;
      }
    };
  }
}

export function setupMenuHandlers(
  onStartLocal: (options: MenuOptions) => void,
  onRestart: () => void,
  onHost?: (roomCode: string) => void,
  onJoin?: (roomCode: string) => void,
  onCampaign?: () => void,
  onSector?: () => void,
): () => void {
  const btnQuickmatch = document.getElementById('btn-quickmatch');
  const btnMultiplayer = document.getElementById('btn-multiplayer');
  const btnCampaign = document.getElementById('btn-campaign');
  const btnLeaderboard = document.getElementById('btn-leaderboard');
  const btnSettings = document.getElementById('btn-settings');
  const btnHelp = document.getElementById('btn-help');
  const btnLeaderboardBack = document.getElementById('btn-leaderboard-back');
  const btnSettingsBack = document.getElementById('btn-settings-back');
  const btnHelpBack = document.getElementById('btn-help-back');
  const btnBack = document.getElementById('btn-back');
  const btnLocal = document.getElementById('btn-local');
  const btnRestart = document.getElementById('btn-restart');
  const btnShopReady = document.getElementById('btn-shop-ready');
  const btnShopBack = document.getElementById('btn-shop-back');
  const btnRosterConfirm = document.getElementById('btn-roster-confirm');
  const btnRosterBack = document.getElementById('btn-roster-back');
  const playersEl = document.getElementById('opt-players');
  const cbpEl = document.getElementById('opt-cbp');

  const quickmatchHandler = () => {
    showQuickmatchConfig();
  };

  const multiplayerHandler = () => {
    showP2PLobby();
  };

  const campaignHandler = () => {
    if (onCampaign) onCampaign();
  };
  const sectorHandler = () => { if (onSector) onSector(); };
  const btnSector = document.getElementById('btn-sector');
  btnSector?.addEventListener('click', sectorHandler);

  const btnHost = document.getElementById('btn-host');
  const btnJoinRoom = document.getElementById('btn-join');
  const btnP2PBack = document.getElementById('btn-p2p-back');

  const hostHandler = () => {
    const roomInput = document.getElementById('opt-room') as HTMLInputElement | null;
    let room = roomInput?.value?.trim().toUpperCase() || '';
    if (!room) {
      room = Math.random().toString(36).substring(2, 8).toUpperCase();
      if (roomInput) roomInput.value = room;
    }
    onHost?.(room);
  };

  const joinHandler = () => {
    const roomInput = document.getElementById('opt-room') as HTMLInputElement | null;
    const room = roomInput?.value?.trim().toUpperCase() || '';
    if (!room) {
      updateP2PStatus('Enter a room code', 'net-waiting');
      return;
    }
    onJoin?.(room);
  };

  const p2pBackHandler = () => {
    showMainMenu();
  };

  const leaderboardHandler = () => {
    showLeaderboard();
  };

  const settingsHandler = () => {
    showSettings();
  };

  const helpHandler = () => {
    showHelp();
  };

  const backHandler = () => {
    showMainMenu();
  };

  // "BEGIN ASSESSMENT" now opens roster screen
  let rosterOptions: MenuOptions | null = null;

  const localHandler = () => {
    runSetupValidation();
    if (hasBlockingWarnings(currentSetupWarnings())) {
      setAdvancedOpen(true);
      return;
    }
    rosterOptions = getMenuOptions();
    buildRosterUI(rosterOptions);
    showRoster();
  };

  // "CONFIRM ROSTER" proceeds to shop
  const rosterConfirmHandler = () => {
    if (!rosterOptions) return;
    const { numPlayers, numBots } = getRosterConfig(rosterOptions);
    rosterOptions.numPlayers = numPlayers;
    rosterOptions.numBots = numBots;
    initShopState(rosterOptions);
    renderShopTabs();
    renderShopItems();
    updateShopRemaining();
    showShop();
  };

  // "Back" from roster returns to config
  const rosterBackHandler = () => {
    rosterOptions = null;
    showQuickmatchConfig();
  };

  // "READY" in shop starts the game (auto-purchase for bots first)
  const shopReadyHandler = () => {
    if (!shopState) return;
    const options = { ...shopState.options };
    const loadouts = getShopLoadouts();

    const numTeams = options.numTeams ?? options.numPlayers;
    const seed = options.seed || String(Date.now());
    // Credits left after shopping, per team — carried into the match (human teams: the shop's remaining)
    const teamStartingCBP: number[] = shopState.playerBudgets.slice(0, numTeams);
    while (teamStartingCBP.length < numTeams) teamStartingCBP.push(options.cbpBudget);

    if (options.unitSlots.length > 0) {
      // Use unitSlots: auto-purchase for teams that have bot units
      for (let t = 0; t < numTeams; t++) {
        const teamSlots = options.unitSlots[t];
        if (!teamSlots) continue;
        const hasBots = teamSlots.some(s => s.type === 'bot');
        const hasHumans = teamSlots.some(s => s.type === 'player');
        if (hasBots && !hasHumans) {
          // Fully bot team — auto-purchase for the team
          // Same rule as humans: one unit's credits buys the kit every unit carries
          const botBudget = options.cbpBudget;
          // Use first bot's personality for loadout selection
          const firstBot = teamSlots.find(s => s.type === 'bot')!;
          const personality = BOT_PRESETS[firstBot.personality] ?? BOT_PRESETS.balanced;
          const rng = new SeededRandom(`${seed}-botshop-${t}`);
          const botLoadout = computeBotLoadout(botBudget, personality, rng, options.gameMode, options.allowedWeapons);
          loadouts[t] = {
            ammo: {
              standard: null,
              heavy: botLoadout.purchases.heavy ?? 0,
              cluster: botLoadout.purchases.cluster ?? 0,
              nuke: botLoadout.purchases.nuke ?? 0,
              probe: botLoadout.purchases.probe ?? 0,
              punch: botLoadout.purchases.punch ?? 0,
            },
            upgrades: {},
          };
          shopState!.playerFuel[t] = botLoadout.fuelPurchased;
          teamStartingCBP[t] = Math.max(0, botBudget - botLoadoutSpend(botLoadout));
        }
        // Mixed teams (human+bot): human purchases via shop, bots share that loadout
      }
    } else {
      // Legacy path: auto-populate bot team loadouts
      const humanTeams = options.humanTeams ?? (options.numPlayers - options.numBots);
      const botBudget = options.cbpBudget; // one unit's credits, kit shared by the team
      for (let i = humanTeams; i < numTeams; i++) {
        const botIndex = i - humanTeams;
        const override = options.botOverrides[botIndex];
        const persKey = override?.personality && override.personality !== 'default'
          ? override.personality : options.botPersonality;
        const personality = BOT_PRESETS[persKey] ?? BOT_PRESETS.balanced;
        const rng = new SeededRandom(`${seed}-botshop-${i}`);
        const botLoadout = computeBotLoadout(botBudget, personality, rng, options.gameMode, options.allowedWeapons);
        loadouts[i] = {
          ammo: {
            standard: null,
            heavy: botLoadout.purchases.heavy ?? 0,
            cluster: botLoadout.purchases.cluster ?? 0,
            nuke: botLoadout.purchases.nuke ?? 0,
            probe: botLoadout.purchases.probe ?? 0,
            punch: botLoadout.purchases.punch ?? 0,
          },
          upgrades: {},
        };
        shopState!.playerFuel[i] = botLoadout.fuelPurchased;
        teamStartingCBP[i] = Math.max(0, botBudget - botLoadoutSpend(botLoadout));
      }
    }

    options.playerLoadouts = loadouts;
    options.playerFuel = shopState!.playerFuel;
    options.teamStartingCBP = teamStartingCBP;
    shopState = null;
    rosterOptions = null;
    onStartLocal(options);
  };

  // "Back" from shop returns to roster
  const shopBackHandler = () => {
    shopState = null;
    if (rosterOptions) {
      showRoster();
    } else {
      showQuickmatchConfig();
    }
  };

  const restartHandler = () => {
    onRestart();
    showMainMenu();
  };

  const playersChangeHandler = () => {
    updatePlayerNameInputs();
  };

  const botsChangeHandler = () => {
    updatePlayerNameInputs();
  };

  const teamsChangeHandler = () => {
    updatePlayerNameInputs();
  };

  const cbpChangeHandler = () => {
    updateCbpDisplay();
  };

  // Initialize armory restriction checkboxes
  initArmoryUI();

  // Quick-play presets first; Advanced below. Skirmish is the default setup.
  renderQuickPresets();
  renderKitGrid();
  renderKitPresets();
  applyQuickPreset('skirmish');
  const advancedToggle = document.getElementById('advanced-toggle');
  const advancedToggleHandler = () => setAdvancedOpen(!isAdvancedOpen());
  if (advancedToggle) advancedToggle.addEventListener('click', advancedToggleHandler);
  const advancedSection = document.getElementById('advanced-section');
  const advancedChangeHandler = (e: Event) => {
    const t = e.target as HTMLElement | null;
    if (!t || !(t instanceof HTMLInputElement || t instanceof HTMLSelectElement)) return;
    markSetupCustom();
    applySetupDefaults(t.id);
    runSetupValidation();
  };
  if (advancedSection) {
    advancedSection.addEventListener('change', advancedChangeHandler);
    advancedSection.addEventListener('input', advancedChangeHandler);
  }

  // Flavor text: plain names by default, the fiction on request
  applyFlavorDom();
  updateFlavorToggles();
  const settingFlavorMenu = document.getElementById('setting-flavor-menu');
  const flavorMenuHandler = () => { setFlavorEnabled(!isFlavorEnabled()); };
  if (settingFlavorMenu) settingFlavorMenu.addEventListener('click', flavorMenuHandler);
  updateDevToggles();
  document.getElementById('btn-lab')?.addEventListener('click', showLab);
  document.getElementById('btn-lab-back')?.addEventListener('click', showMainMenu);
  const settingDevMenu = document.getElementById('setting-dev-menu');
  const devMenuHandler = () => { setDevMode(!isDevMode()); };
  if (settingDevMenu) settingDevMenu.addEventListener('click', devMenuHandler);
  // The real-time buttons' scale (human 1/20 by default; classic for comparison)
  updateActionScaleToggles();
  document.getElementById('setting-action-scale-menu')?.addEventListener('click', () => { setActionScale(actionScale() === 'human' ? 'classic' : 'human'); });

  if (btnQuickmatch) {
    btnQuickmatch.addEventListener('click', quickmatchHandler);
  }

  if (btnMultiplayer) {
    btnMultiplayer.addEventListener('click', multiplayerHandler);
  }

  if (btnCampaign) {
    btnCampaign.addEventListener('click', campaignHandler);
  }

  if (btnHost) btnHost.addEventListener('click', hostHandler);
  if (btnJoinRoom) btnJoinRoom.addEventListener('click', joinHandler);
  if (btnP2PBack) btnP2PBack.addEventListener('click', p2pBackHandler);

  if (btnLeaderboard) {
    btnLeaderboard.addEventListener('click', leaderboardHandler);
  }

  if (btnSettings) {
    btnSettings.addEventListener('click', settingsHandler);
  }

  if (btnHelp) {
    btnHelp.addEventListener('click', helpHandler);
  }

  if (btnLeaderboardBack) {
    btnLeaderboardBack.addEventListener('click', backHandler);
  }

  if (btnSettingsBack) {
    btnSettingsBack.addEventListener('click', backHandler);
  }

  if (btnHelpBack) {
    btnHelpBack.addEventListener('click', backHandler);
  }

  if (btnBack) {
    btnBack.addEventListener('click', backHandler);
  }

  if (btnLocal) {
    btnLocal.addEventListener('click', localHandler);
  }

  if (btnShopReady) {
    btnShopReady.addEventListener('click', shopReadyHandler);
  }

  if (btnShopBack) {
    btnShopBack.addEventListener('click', shopBackHandler);
  }

  if (btnRosterConfirm) {
    btnRosterConfirm.addEventListener('click', rosterConfirmHandler);
  }

  if (btnRosterBack) {
    btnRosterBack.addEventListener('click', rosterBackHandler);
  }

  if (btnRestart) {
    btnRestart.addEventListener('click', restartHandler);
  }

  const botsEl2 = document.getElementById('opt-bots');

  // Populate company selectors from TEAMS array
  populateCompanySelectors();

  if (playersEl) {
    playersEl.addEventListener('change', playersChangeHandler);
    // Initialize visibility on load
    updatePlayerNameInputs();
  }

  if (botsEl2) {
    botsEl2.addEventListener('change', botsChangeHandler);
  }

  const numTeamsEl2 = document.getElementById('opt-num-teams');
  const botTeamsEl2 = document.getElementById('opt-bot-teams');
  if (numTeamsEl2) numTeamsEl2.addEventListener('change', teamsChangeHandler);
  if (botTeamsEl2) botTeamsEl2.addEventListener('change', teamsChangeHandler);

  if (cbpEl) {
    cbpEl.addEventListener('input', cbpChangeHandler);
  }

  // System preset & star type handlers
  const presetEl = document.getElementById('opt-system-preset') as HTMLSelectElement | null;
  const starTypeEl = document.getElementById('opt-star-type') as HTMLSelectElement | null;

  const presetChangeHandler = () => {
    if (presetEl) {
      applySystemPreset(presetEl.value as SystemPreset);
    }
  };

  const starTypeChangeHandler = () => {
    // Changing star type manually resets preset to Custom
    if (presetEl) presetEl.value = 'custom';
    updateSystemDescription('custom');
  };

  // Any other config field change resets preset to Custom
  const resetPresetHandler = () => {
    if (presetEl && presetEl.value !== 'custom') {
      presetEl.value = 'custom';
      updateSystemDescription('custom');
    }
  };

  if (presetEl) presetEl.addEventListener('change', presetChangeHandler);
  if (starTypeEl) starTypeEl.addEventListener('change', starTypeChangeHandler);

  // Mode change handler: show preset desc + run validation
  const gameModeEl = document.getElementById('opt-game-mode') as HTMLSelectElement | null;
  const modeChangeHandler = () => {
    const mode = (gameModeEl?.value ?? 'deathmatch') as GameMode;
    updateModeDescription(mode);
    runSetupValidation();
  };
  if (gameModeEl) {
    gameModeEl.addEventListener('change', modeChangeHandler);
  }
  // Modifier toggles re-run validation (a stacked environment has its own planet-count needs)
  for (const id of ['opt-mod-blackout', 'opt-mod-lastlight', 'opt-mod-stealth']) {
    document.getElementById(id)?.addEventListener('change', runSetupValidation);
  }

  // Mode preset apply button
  const presetApplyBtn = document.getElementById('btn-apply-mode-preset');
  const presetApplyHandler = () => {
    const mode = (gameModeEl?.value ?? 'deathmatch') as GameMode;
    applyModePreset(mode);
    runSetupValidation();
  };
  if (presetApplyBtn) {
    presetApplyBtn.addEventListener('click', presetApplyHandler);
  }

  // Mode rules "?" button
  const modeRulesBtn = document.getElementById('btn-mode-rules');
  const modeRulesPopup = document.getElementById('mode-rules-popup');
  const modeRulesHandler = () => {
    if (!modeRulesPopup) return;
    const visible = modeRulesPopup.style.display !== 'none';
    if (visible) {
      modeRulesPopup.style.display = 'none';
    } else {
      const mode = (gameModeEl?.value ?? 'deathmatch') as GameMode;
      const info = MODE_RULES[mode];
      if (info) {
        const bullets = info.rules.map(r => `\u2022 ${r}`).join('<br>');
        modeRulesPopup.innerHTML =
          `<span style="color:${theme.text.modeRule};"><strong>${info.objective}</strong></span><br>`
          + `<span style="opacity:0.7;">${bullets}</span>`;
      }
      modeRulesPopup.style.display = '';
    }
  };
  if (modeRulesBtn) {
    modeRulesBtn.addEventListener('click', modeRulesHandler);
  }
  // Hide rules popup when mode changes
  const hideModeRules = () => { if (modeRulesPopup) modeRulesPopup.style.display = 'none'; };
  if (gameModeEl) gameModeEl.addEventListener('change', hideModeRules);

  // Attach reset-preset to all other config dropdowns
  const configResetIds = ['opt-planets', 'opt-stars', 'opt-wormholes', 'opt-planet-size', 'opt-map-layout', 'opt-hazards'];
  for (const id of configResetIds) {
    const el = document.getElementById(id);
    if (el) el.addEventListener('change', resetPresetHandler);
  }

  return () => {
    if (advancedToggle) advancedToggle.removeEventListener('click', advancedToggleHandler);
    if (advancedSection) {
      advancedSection.removeEventListener('change', advancedChangeHandler);
      advancedSection.removeEventListener('input', advancedChangeHandler);
    }
    if (settingFlavorMenu) settingFlavorMenu.removeEventListener('click', flavorMenuHandler);
    if (settingDevMenu) settingDevMenu.removeEventListener('click', devMenuHandler);
    if (btnQuickmatch) {
      btnQuickmatch.removeEventListener('click', quickmatchHandler);
    }
    if (btnMultiplayer) btnMultiplayer.removeEventListener('click', multiplayerHandler);
    if (btnCampaign) btnCampaign.removeEventListener('click', campaignHandler);
    if (btnHost) btnHost.removeEventListener('click', hostHandler);
    if (btnJoinRoom) btnJoinRoom.removeEventListener('click', joinHandler);
    if (btnP2PBack) btnP2PBack.removeEventListener('click', p2pBackHandler);
    if (btnLeaderboard) {
      btnLeaderboard.removeEventListener('click', leaderboardHandler);
    }
    if (btnSettings) {
      btnSettings.removeEventListener('click', settingsHandler);
    }
    if (btnHelp) {
      btnHelp.removeEventListener('click', helpHandler);
    }
    if (btnLeaderboardBack) {
      btnLeaderboardBack.removeEventListener('click', backHandler);
    }
    if (btnSettingsBack) {
      btnSettingsBack.removeEventListener('click', backHandler);
    }
    if (btnHelpBack) {
      btnHelpBack.removeEventListener('click', backHandler);
    }
    if (btnBack) {
      btnBack.removeEventListener('click', backHandler);
    }
    if (btnLocal) {
      btnLocal.removeEventListener('click', localHandler);
    }
    if (btnShopReady) {
      btnShopReady.removeEventListener('click', shopReadyHandler);
    }
    if (btnShopBack) {
      btnShopBack.removeEventListener('click', shopBackHandler);
    }
    if (btnRosterConfirm) {
      btnRosterConfirm.removeEventListener('click', rosterConfirmHandler);
    }
    if (btnRosterBack) {
      btnRosterBack.removeEventListener('click', rosterBackHandler);
    }
    if (btnRestart) {
      btnRestart.removeEventListener('click', restartHandler);
    }
    if (playersEl) {
      playersEl.removeEventListener('change', playersChangeHandler);
    }
    if (botsEl2) {
      botsEl2.removeEventListener('change', botsChangeHandler);
    }
    if (numTeamsEl2) numTeamsEl2.removeEventListener('change', teamsChangeHandler);
    if (botTeamsEl2) botTeamsEl2.removeEventListener('change', teamsChangeHandler);
    if (cbpEl) {
      cbpEl.removeEventListener('input', cbpChangeHandler);
    }
    if (gameModeEl) gameModeEl.removeEventListener('change', modeChangeHandler);
    if (presetApplyBtn) presetApplyBtn.removeEventListener('click', presetApplyHandler);
  };
}

// =============================================================================
// MODE PRESETS & VALIDATION
// =============================================================================

/** Environment layers ticked under the mode select. */
function readModifiers(): EnvironmentKey[] {
  const mods: EnvironmentKey[] = [];
  if ((document.getElementById('opt-mod-blackout') as HTMLInputElement | null)?.checked) mods.push('blackout');
  if ((document.getElementById('opt-mod-lastlight') as HTMLInputElement | null)?.checked) mods.push('lastLight');
  return mods;
}

function updateModeDescription(mode: GameMode): void {
  const descEl = document.getElementById('mode-description');
  if (descEl) {
    descEl.textContent = fx(MODE_PRESET_DESCRIPTIONS[mode] ?? '', MODE_FLAVOR_DESCRIPTIONS[mode] ?? '');
  }
}

function applyModePreset(mode: GameMode): void {
  const preset = getModePreset(mode);

  const turnLimitEl = document.getElementById('opt-turn-limit') as HTMLInputElement | null;
  const numTeamsEl = document.getElementById('opt-num-teams') as HTMLSelectElement | null;
  const planetsEl = document.getElementById('opt-planets') as HTMLSelectElement | null;

  if (turnLimitEl) {
    turnLimitEl.value = preset.suggestedTurnLimit !== undefined ? String(preset.suggestedTurnLimit) : '';
  }

  if (numTeamsEl) numTeamsEl.value = String(preset.suggestedTeams);
  if (planetsEl && !isSystemLocked()) planetsEl.value = String(preset.suggestedPlanets);
  clampBotTeams();
  updatePlayerNameInputs();

  updateModeDescription(mode);
}

function runSetupValidation(): void {
  const warningsEl = document.getElementById('mode-warnings');
  if (!warningsEl) return;
  const warnings = currentSetupWarnings();

  if (warnings.length === 0) {
    warningsEl.textContent = '';
    warningsEl.style.display = 'none';
    return;
  }

  warningsEl.style.display = 'block';
  warningsEl.innerHTML = warnings.map(w => {
    const icon = w.severity === 'block' ? '⊘' : '⚠';
    const color = w.severity === 'block' ? theme.warningError : theme.warningAmber;
    return `<div style="color:${color};margin:2px 0">${icon} ${w.message}</div>`;
  }).join('');
}
